import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';
import { categoryMap } from '@/mocks/categories';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  const category = categoryMap[article.category];

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
      >
        <div className="flex flex-col lg:flex-row">
          <div className="lg:w-2/5 h-56 lg:h-auto relative overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 援助交配现象研究`}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            />
          </div>
          <div className="lg:w-3/5 p-6 md:p-8 flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              {category && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 whitespace-nowrap">
                  {category.name}
                </span>
              )}
              <span className="text-xs text-foreground-500 whitespace-nowrap">{article.publishDate}</span>
              <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime} 分钟阅读</span>
            </div>
            <h3 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-3 group-hover:text-primary-600 transition-colors duration-200">
              {article.title}
            </h3>
            <p className="text-foreground-600 leading-relaxed line-clamp-3">{article.excerpt}</p>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {article.tags.slice(0, 3).map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded whitespace-nowrap">
                  #{tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="w-20 h-20 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageThumbUrl}
            alt={article.title}
            title={`${article.title} - 援助交配现象研究`}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1 whitespace-nowrap">{article.publishDate} · {article.readTime} 分钟</p>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="h-48 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          title={`${article.title} - 援助交配现象研究`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          {category && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-accent-100 text-accent-800 whitespace-nowrap">
              {category.name}
            </span>
          )}
          <span className="text-xs text-foreground-500 whitespace-nowrap">{article.readTime} 分钟</span>
        </div>
        <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-600 leading-relaxed line-clamp-2">{article.excerpt}</p>
      </div>
    </Link>
  );
}